import React from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import CreatomateVideoEditDialog from "./CreatomateVideoEditDialog";
import VreVideoEditDialog from "./VreVideoEditDialog";
import useRenderEngine from "./useRenderEngine";

/**
 * The video editor for whichever render engine CreatorVault runs on
 * (RENDER_ENGINE on the backend, read from GET /mrss/render-engine/):
 * WooGlobe VRE's editor, or the Creatomate editor used before it.
 *
 * Same props either way. `onSave` hands back that engine's own render payload
 *   VRE         [{ engine: "vre", template_id, template_version, document }]
 *   Creatomate  [{ template_id, modifications }]
 * and the backend renders each with the engine it was made for.
 *
 * Push to Admin Portal is VRE-only and uses VreVideoEditDialog directly.
 */

// A brand's template snapshot reaches the dialog as `document`. For the
// Creatomate editor that snapshot is the template's `source`; without one the
// editor opens its template gallery, as it did before VRE.
function asCreatomateTemplate(template) {
  if (!template) return null;
  const source = template.source ?? template.document ?? null;
  return source ? { ...template, source } : null;
}

function VideoEditDialog(props) {
  const { open, onClose, preselectedTemplate } = props;
  const { engine, error } = useRenderEngine(open);

  if (engine === "creatomate") {
    return (
      <CreatomateVideoEditDialog {...props} preselectedTemplate={asCreatomateTemplate(preselectedTemplate)} />
    );
  }
  if (engine === "vre") {
    return <VreVideoEditDialog {...props} />;
  }
  if (!open) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <Box display="flex" alignItems="center" gap={2} py={2}>
          {error ? (
            <Typography color="error">
              {error?.response?.status === 401
                ? "The video editor could not be opened: your login has expired. Log out, log in again, and reopen the editor."
                : "The video editor could not be opened: its settings did not load. Check the connection and try again."}
            </Typography>
          ) : (
            <>
              <CircularProgress size={24} />
              <Typography>Opening the video editor…</Typography>
            </>
          )}
        </Box>
      </DialogContent>
      {error && (
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default VideoEditDialog;
